import React, { useEffect, useState } from 'react';
import '../styles/components/home-page.scss';
import Countdown from '../components/Countdown';
import { useEvent } from '../context/EventContext';
import { useNews, Article } from '../context/NewsContext';
import { Link } from 'react-router-dom';
import { baseURL, baseURLnoapi, CURRENT_SEASON } from '../Constants';
import TeamCarousel from '../components/TeamCarousel'; // Import the TeamCarousel component

// Define Team interface based on the C# model
interface Team {
  id: string;
  name: string;
  logo: string | null;
  players: any[]; // We could define a Player interface if needed
}

const HomePage: React.FC = () => {
  const { nextLanEvent } = useEvent();
  const { getLatestNewsArticles, isLoading: isNewsLoading } = useNews();
  const [news, setNews] = useState<Article[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isTeamsLoading, setIsTeamsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Get the latest news from the context (excluding info content)
    const fetchNews = async () => {
      const latestNews = await getLatestNewsArticles(4); // Fetch 4 articles instead of 3
      setNews(latestNews);
    };
    
    // Create placeholder teams for coming soon
    const createPlaceholderTeams = () => {
      const placeholderLogo = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23222'/%3E%3Ctext x='50' y='50' font-size='40' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23666'%3E?%3C/text%3E%3C/svg%3E";

      return Array.from({ length: 8 }, (_, i) => ({
        id: `placeholder-${i + 1}`,
        name: 'Coming Soon',
        logo: placeholderLogo,
        players: []
      }));
    };

    // Fetch teams from the API
    const fetchTeams = async () => {
      setIsTeamsLoading(true);
      try {
        // First, get basic team list
        const response = await fetch(`${baseURL}/Team/basic/${CURRENT_SEASON}`);
        if (!response.ok) {
          throw new Error('Failed to fetch teams');
        }

        const basicTeamsData = await response.json();

        // Filter out TBD team (used for backend data only, not public display)
        const filteredTeams = basicTeamsData.filter((team: Team) =>
          team.name.toUpperCase() !== 'TBD'
        );

        // If no teams, show all placeholders
        if (!filteredTeams || filteredTeams.length === 0) {
          setTeams(createPlaceholderTeams());
          return;
        }

        // Fetch full details for each team (including players) from local API and PowerStats
        const teamsWithPlayers = await Promise.all(
          filteredTeams.slice(0, 8).map(async (team: Team) => {
            try {
              // Fetch team with player data from local API (to get current roster)
              const teamResponse = await fetch(`${baseURL}/Team/${team.id}?season=${CURRENT_SEASON}`);

              if (teamResponse.ok) {
                const teamData = await teamResponse.json();

                // Try to fetch player stats from PowerStats, trying multiple seasons if needed
                let powerStatsPlayers: any[] = [];

                // Try seasons from current down to 1
                for (let season = CURRENT_SEASON; season >= 1; season--) {
                  try {
                    const powerStatsResponse = await fetch(
                      `https://api.rising.powerstats.dk/RisingStats/api/PlayerStat/getPlayersByTeamId?teamid=${team.id}&season=${season}`
                    );

                    if (powerStatsResponse.ok) {
                      const powerStatsData = await powerStatsResponse.json();

                      // Check if we got players with stats
                      if (powerStatsData && Array.isArray(powerStatsData) && powerStatsData.length > 0) {
                        powerStatsPlayers = powerStatsData;
                        break; // Found data, stop searching
                      }
                    }
                  } catch (seasonErr) {
                    // Continue to next season
                  }
                }

                // Map local team players to include ratings from PowerStats
                const playersWithRatings = (teamData.players || []).map((player: any) => {
                  // Find matching player in PowerStats data
                  const powerStatsPlayer = powerStatsPlayers.find(
                    (p: any) => p.steamID === player.steamID
                  );

                  return {
                    steamID: player.steamID,
                    playerName: player.playerName,
                    rating: powerStatsPlayer?.hltv2Rating || 0,
                  };
                });

                return {
                  ...team,
                  players: playersWithRatings
                };
              }

              // If team fetch fails, return team with empty players
              return { ...team, players: [] };
            } catch (err) {
              console.error(`Error fetching details for team ${team.id}:`, err);
              return { ...team, players: [] };
            }
          })
        );

        // Fill remaining slots with placeholders if needed
        if (teamsWithPlayers.length < 8) {
          const placeholderLogo = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23222'/%3E%3Ctext x='50' y='50' font-size='40' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23666'%3E?%3C/text%3E%3C/svg%3E";

          const remainingSlots = 8 - teamsWithPlayers.length;
          const placeholders = Array.from({ length: remainingSlots }, (_, i) => ({
            id: `placeholder-${teamsWithPlayers.length + i + 1}`,
            name: 'Coming Soon',
            logo: placeholderLogo,
            players: []
          }));

          setTeams([...teamsWithPlayers, ...placeholders]);
        } else {
          setTeams(teamsWithPlayers);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        // Show placeholder teams on error
        setTeams(createPlaceholderTeams());
      } finally {
        setIsTeamsLoading(false);
      }
    };
    
    fetchNews();
    fetchTeams();
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Set page title
    document.title = 'Northern Championships Series - Premier Esports Tournament';
  }, [getLatestNewsArticles]);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section 
        className="hero-section"
        style={{ 
          backgroundImage: "url('/images/teamshot1.jpg')",
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="overlay"></div>
        
        <div className="hero-content">
          <h1>Northern Championships Series</h1>
          
          <div className="event-container">
            <div className="event-name">Winter invitational LAN showdown</div>
            <div className="event-date">28/11 - 30/11 Starting at 20:00 GMT+1</div>
            <div className="event-location"><span className="location-icon">📍</span> Sanddal Bakke 21, 7000 Fredericia</div>
          </div>
          
          <div className="countdown-container">
            <Countdown targetDate={nextLanEvent.startDate} endDate={nextLanEvent.endDate} />
          </div>
        </div>
      </section>

      {/* Teams Section with Carousel */}
      <section className="teams-section">
        <div className="container">
          <h2 className="section-title">Participating Teams</h2>
          
          <TeamCarousel 
            teams={teams}
            isLoading={isTeamsLoading}
            baseURL={baseURL || ''}
            baseURLnoapi={baseURLnoapi}
            autoPlayInterval={5000} // Change slides every 5 seconds
            teamsPerView={4} // Show 4 teams at once
          />
        </div>
      </section>

      {/* Latest News Section */}
      <section className="news-section">
        <div className="news-container">
          <h2 className="section-title">Latest News</h2>
          {isNewsLoading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div className="news-grid">
              {news.map(article => (
                <article key={article.id} className="news-card">
                  <div className="news-image">
                    <img 
                      src={article.imageUrl?.startsWith('http') 
                        ? article.imageUrl 
                        : `${baseURLnoapi || ''}${article.imageUrl}`} 
                      alt={article.title} 
                    />
                    <div className="news-category">{article.category}</div>
                  </div>
                  <div className="news-content">
                    <div className="news-date">{new Date(article.date).toLocaleDateString()}</div>
                    <h3>{article.title}</h3>
                    <p>{article.excerpt}</p>
                    <Link to={`/news/${article.id}`} className="read-more">
                      Read More →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
          <div className="more-news">
            <Link to="/news" className="btn btn-outline">View All News</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;